import { useForm } from 'react-hook-form';
import { NavLink, useNavigate } from 'react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircleX } from 'lucide-react';
import { toast } from 'sonner';

import {
  Form,
  FormControl,
  FormItem,
  Input,
  Button,
  FormLabel,
  Card,
} from '@predoc/cow';
import { Footer } from './Footer';
import { AuthBrandLogo } from './AuthBrandLogo';
import { resetPasswordSchema, type ResetPasswordFormValues } from '../types';
import { AUTH_ERRORS, AUTH_SUCCESS, AUTH_PATHS } from '../constants';
import { useResetPassword } from '../hooks/authApi';
import { usePageTitle } from '@/hooks';

export const ResetPassword = () => {
  usePageTitle('Reset Password');
  const navigate = useNavigate();
  const { mutateAsync, isPending, error } = useResetPassword();

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    reValidateMode: 'onBlur',
    defaultValues: {
      email: '',
    },
  });

  const handleSubmit = async (values: ResetPasswordFormValues) => {
    try {
      await mutateAsync({ email: values.email });
      navigate(
        `${AUTH_PATHS.RESET_PASSWORD}?email=${encodeURIComponent(values.email)}`,
      );
      toast.success(AUTH_SUCCESS.RESET_LINK_SENT);
    } catch {
      toast.error(AUTH_ERRORS.RESET_LINK_FAILED);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-between bg-white">
      <div className="flex w-full flex-1 flex-col items-center justify-center px-4">
        <AuthBrandLogo className="mb-8" />

        <Card className="w-full max-w-[464px] p-8">
          <h1 className="mb-6 text-2xl font-semibold tracking-tight">
            Forgot Password
          </h1>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-6"
            >
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="ex. jdoe@email.com"
                    {...form.register('email')}
                    aria-label="Email input"
                  />
                </FormControl>
              </FormItem>

              {(error || form.formState.errors.email) && (
                <div className="flex items-center gap-2">
                  <CircleX className="h-4 w-4 text-red-500" />
                  <p className="text-sm text-red-500">
                    {AUTH_ERRORS.INVALID_EMAIL}
                  </p>
                </div>
              )}

              <Button
                type="submit"
                className="h-11 w-full"
                disabled={!form.formState.isValid || isPending}
                loading={isPending}
              >
                {isPending ? 'Sending...' : 'Request Confirmation Code'}
              </Button>

              <p className="text-center text-sm text-gray-600">
                Back to{' '}
                <NavLink
                  to={AUTH_PATHS.LOGIN}
                  className="text-gray-900 underline"
                >
                  Login
                </NavLink>
              </p>
            </form>
          </Form>
        </Card>
      </div>
      <Footer />
    </div>
  );
};
